import * as shallowEqual from 'shallowequal';
import * as _ from 'underscore';

import { FeatureFlags } from '../../../../ts/FeatureFlags';
import { ServerFilterClauses } from '../../../../ts/helpers/ServerFilterClauses';
import { ExplorableItemType } from '../../../../ts/models/ContentType';
import {
  FilterFieldNames,
  IFieldFilterClause,
  IFilterClause,
  IStringValuedFilterClause
} from '../../../../ts/ServerApi/types/IFilterClause';
import { FilterMap } from '../../../state/ContentExplorerState';
import { SpaceInfo, SpaceType } from '../../../state/SpaceInfo';
import { ContentFilterAppliedAction, FilterContentAction } from './FilterActions';

export type InterfaceForContentFilterActions = ContentFilterAppliedAction | FilterContentAction;

export function doesActionCreateDifferentFilter(currentFilter: IStringValuedFilterClause, action: InterfaceForContentFilterActions): boolean {
  const isBrandNewFilterValue = (_.isUndefined(currentFilter) && action.value !== undefined);  // must be undefined because 'null' is a value for showEmbedded filter
  const isOldValueDifferent = (!_.isUndefined(currentFilter) && actionValueDiffersFromFilter(currentFilter, action));
  return isBrandNewFilterValue || isOldValueDifferent;
}

export function doesActionCreateSameFilter(currentFilter: IStringValuedFilterClause, action: InterfaceForContentFilterActions): boolean {
  return !doesActionCreateDifferentFilter(currentFilter, action);
}

function actionValueDiffersFromFilter(currentFilter: IStringValuedFilterClause, action: InterfaceForContentFilterActions): boolean {
  const filterCreatedFromAction = ServerFilterClauses.serverFilterClauses[action.filterType](action.value);
  return !shallowEqual(filterCreatedFromAction, currentFilter);
}

export function cloneFilterMap(original: FilterMap): FilterMap {
  return _.mapObject(original, (filter: IFilterClause) => (filter === null? null : { ...filter}));
}

export function getSharedWithMeContentTypeFilterClauses(requestedContentTypes: ReadonlyArray<ExplorableItemType>): IFilterClause[] {
  return [ServerFilterClauses.ContentType(requestedContentTypes)];
}

export function hasMultipleContentTypes(shownContentTypes: ReadonlyArray<ExplorableItemType>) {
  return shownContentTypes.length !== 1;
}

export function containsFilterClause(filters: IFilterClause[], filterField: string) {
  return _.some(filters,
    clause => clause && (clause as IFieldFilterClause).field === filterField);
}

export function removeFilterClauseFromFilters(filters: IFilterClause[], filterField: string) {
  return _.filter(filters,
    clause => !!clause && (hasNoField(clause) || (clause as IFieldFilterClause).field !== filterField));
}

export function adjustFiltersForSiteUserSpaceCollectionType(filters: IFilterClause[]) {

  if (!FeatureFlags.useCollectionsFavorites) {
    filters = removeFilterClauseFromFilters(filters, FilterFieldNames.isFavorite);
  }

  filters = _.map(filters, clause => {
    if (!hasNoField(clause)) {
      if ((clause as IFieldFilterClause).field === FilterFieldNames.createdAt) {
        return {...clause, field: FilterFieldNames.createdTime};
      } else if ((clause as IFieldFilterClause).field === FilterFieldNames.updatedAt) {
        return {...clause, field: FilterFieldNames.updatedTime};
      }
    }
    return clause;
  });

  return filters;
}

function hasNoField(clause: any): boolean {
  return !(clause as IFieldFilterClause).field;
}

type SpaceSupportsStylishFiltering = {
  readonly [P in SpaceType]: boolean;
};

const spaceSupportsStylishFiltering: SpaceSupportsStylishFiltering = {
  'sites': true,
  'favorites': true,
  'recents': true,
  'project': true,
  'site': true,
  'siteuser': true,
  'search': true,
  'unifiedSearch': true,
  'backgroundJobs': true,
  'metric': true,
  'sitetasks': true,
  'siteusers': true,
  'serverusers': true,
  'group': true,
  'personal': true,
  'collections': true,
  'collection': true,

  'flow': false,
  'workbook': false,
  'dataRole': false,
  'lens': false,
  'datasource': false,
  'home': false,
  'recommendations': false,
  'view': false,
  'sitestatus': false,
  'schedules': false,
  'schedule': false,
  'siteanalysis': false,
  'serveruser': false,
  'serverstatus': false,
  'serveranalysis': false,
  'sitesettings': false,
  'serversettings': false,
  'servertasks': false,
  'catalog': false,
  'groups': false,
  'revisionhistory': false,
  'publishedConnection': false,
  'publishedConnectionTable': false,
  'starters': false,
  'sharedWithMe': false,
  'resourceGovernanceSetting': false,
  'externalAssets': false,
  'connectedApplication': false,
};

// spaces that are already styled (e.g., not groups) and support filtering
export function doesSpaceSupportStylishFiltering(spaceInfo: SpaceInfo): boolean {
  return spaceSupportsStylishFiltering[spaceInfo.spaceType];
}
